/**
 * Admin UI for Local Government custom sections (Phase 1) and custom charts (Phase 2).
 * List sections and datasets by page; Add / Edit / Delete via modals.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchAllCustomSectionsForLocation,
  createCustomSection,
  updateCustomSection,
  deleteCustomSection,
  getAllowedPageIds,
  pageIdToLabel,
  slugFromTitle,
  type CustomSection,
  type CustomSectionPageId,
  type CustomSectionMetricInsert
} from '../../services/customSectionsService';
import {
  fetchAllCustomDatasetsForLocation,
  createCustomDataset,
  updateCustomDataset,
  deleteCustomDataset,
  parseCsvToRows,
  getAllowedDatasetPageIds,
  getChartTypes,
  chartTypeToLabel,
  type CustomDataset,
  type CustomDatasetPageId,
  type CustomDatasetChartType
} from '../../services/customDatasetsService';
import './AdminCustomContent.css';

interface AdminCustomContentProps {
  locationName: string;
  userId: string;
  scopeLabel?: string;
}

const emptyMetric = (): CustomSectionMetricInsert => ({ section_id: '', label: '', value: '', color_hex: null });

export const AdminCustomContent: React.FC<AdminCustomContentProps> = ({ locationName, userId, scopeLabel }) => {
  const [sections, setSections] = useState<CustomSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<CustomSection | null>(null);
  const [form, setForm] = useState({
    page_id: 'transportation' as CustomSectionPageId,
    section_title: '',
    section_slug: '',
    content_html: '',
    overview_html: '',
    relevance_html: '',
    equity_html: '',
    methodology_html: '',
    overview_title: '',
    relevance_title: '',
    equity_title: '',
    methodology_title: '',
    image_url: '',
    link_url: '',
    link_label: '',
    is_published: true,
    metrics: [emptyMetric()]
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Custom charts (Phase 2)
  const [datasets, setDatasets] = useState<CustomDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(true);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [editingDataset, setEditingDataset] = useState<CustomDataset | null>(null);
  const emptyChartMetric = (): { label: string; value: string; color_hex: string | null } => ({ label: '', value: '', color_hex: null });
  const [chartForm, setChartForm] = useState({
    page_id: 'transportation' as CustomDatasetPageId,
    title: '',
    chart_type: 'time_series' as CustomDatasetChartType,
    description: '',
    csvText: '',
    is_published: true,
    metrics: [emptyChartMetric()] as { label: string; value: string; color_hex: string | null }[],
    overview_html: '',
    relevance_html: '',
    equity_html: '',
    methodology_html: '',
    overview_title: '',
    relevance_title: '',
    equity_title: '',
    methodology_title: ''
  });
  const [chartSaving, setChartSaving] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);

  const loadSections = useCallback(async () => {
    if (!locationName) return;
    setLoading(true);
    const data = await fetchAllCustomSectionsForLocation(locationName);
    setSections(data);
    setLoading(false);
  }, [locationName]);

  const loadDatasets = useCallback(async () => {
    if (!locationName) return;
    setDatasetsLoading(true);
    const data = await fetchAllCustomDatasetsForLocation(locationName);
    setDatasets(data);
    setDatasetsLoading(false);
  }, [locationName]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  const openAdd = (pageId?: CustomSectionPageId) => {
    setEditingSection(null);
    setForm({
      page_id: pageId || 'transportation',
      section_title: '',
      section_slug: '',
      content_html: '',
      overview_html: '',
      relevance_html: '',
      equity_html: '',
      methodology_html: '',
      overview_title: '',
      relevance_title: '',
      equity_title: '',
      methodology_title: '',
      image_url: '',
      link_url: '',
      link_label: '',
      is_published: true,
      metrics: [emptyMetric()]
    });
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (section: CustomSection) => {
    setEditingSection(section);
    setForm({
      page_id: section.page_id,
      section_title: section.section_title,
      section_slug: section.section_slug,
      content_html: section.content_html || '',
      overview_html: section.overview_html || '',
      relevance_html: section.relevance_html || '',
      equity_html: section.equity_html || '',
      methodology_html: section.methodology_html || '',
      overview_title: section.overview_title || '',
      relevance_title: section.relevance_title || '',
      equity_title: section.equity_title || '',
      methodology_title: section.methodology_title || '',
      image_url: section.image_url || '',
      link_url: section.link_url || '',
      link_label: section.link_label || '',
      is_published: section.is_published,
      metrics:
        section.metrics && section.metrics.length > 0
          ? section.metrics.map((m) => ({
              section_id: section.id,
              label: m.label,
              value: m.value,
              display_order: m.display_order,
              color_hex: m.color_hex ?? undefined
            }))
          : [emptyMetric()]
    });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingSection(null);
  };

  const updateForm = (updates: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const updateMetric = (index: number, field: keyof CustomSectionMetricInsert, value: string | number) => {
    setForm((prev) => {
      const next = [...prev.metrics];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, metrics: next };
    });
  };

  const addMetric = () => {
    setForm((prev) => ({ ...prev, metrics: [...prev.metrics, emptyMetric()] }));
  };

  const removeMetric = (index: number) => {
    setForm((prev) => ({
      ...prev,
      metrics: prev.metrics.filter((_, i) => i !== index)
    }));
  };

  const handleTitleChange = (title: string) => {
    updateForm({ section_title: title });
    if (!editingSection) updateForm({ section_slug: slugFromTitle(title) });
  };

  const handleSave = async () => {
    setError(null);
    const slug = form.section_slug.trim() || slugFromTitle(form.section_title);
    if (!form.section_title.trim()) {
      setError('Title is required.');
      return;
    }

    const metricsFiltered = form.metrics
      .filter((m) => m.label.trim() || m.value.trim())
      .map((m, i) => ({
        section_id: editingSection?.id ?? '',
        label: m.label.trim(),
        value: m.value.trim(),
        display_order: i,
        color_hex: m.color_hex || null
      }));

    setSaving(true);
    try {
      if (editingSection) {
        const updated = await updateCustomSection(
          editingSection.id,
          {
            page_id: form.page_id,
            section_title: form.section_title.trim(),
            section_slug: slug,
            content_html: form.content_html.trim() || null,
            overview_html: form.overview_html.trim() || null,
            relevance_html: form.relevance_html.trim() || null,
            equity_html: form.equity_html.trim() || null,
            methodology_html: form.methodology_html.trim() || null,
            overview_title: form.overview_title.trim() || null,
            relevance_title: form.relevance_title.trim() || null,
            equity_title: form.equity_title.trim() || null,
            methodology_title: form.methodology_title.trim() || null,
            image_url: form.image_url.trim() || null,
            link_url: form.link_url.trim() || null,
            link_label: form.link_label.trim() || null,
            is_published: form.is_published
          },
          metricsFiltered,
          userId
        );
        if (updated) {
          await loadSections();
          closeModal();
        } else {
          setError('Failed to update section.');
        }
      } else {
        const created = await createCustomSection(
          {
            location_name: locationName,
            page_id: form.page_id,
            section_title: form.section_title.trim(),
            section_slug: slug,
            content_html: form.content_html.trim() || null,
            overview_html: form.overview_html.trim() || null,
            relevance_html: form.relevance_html.trim() || null,
            equity_html: form.equity_html.trim() || null,
            methodology_html: form.methodology_html.trim() || null,
            overview_title: form.overview_title.trim() || null,
            relevance_title: form.relevance_title.trim() || null,
            equity_title: form.equity_title.trim() || null,
            methodology_title: form.methodology_title.trim() || null,
            image_url: form.image_url.trim() || null,
            link_url: form.link_url.trim() || null,
            link_label: form.link_label.trim() || null,
            display_order: sections.filter((s) => s.page_id === form.page_id).length,
            is_published: form.is_published,
            created_by: userId
          },
          metricsFiltered,
          userId
        );
        if (created) {
          await loadSections();
          closeModal();
        } else {
          setError('Failed to create section.');
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (section: CustomSection) => {
    if (!window.confirm(`Delete "${section.section_title}"?`)) return;
    const ok = await deleteCustomSection(section.id);
    if (ok) await loadSections();
  };

  const openAddChart = (pageId?: CustomDatasetPageId) => {
    setEditingDataset(null);
    setChartForm({
      page_id: pageId || 'transportation',
      title: '',
      chart_type: 'time_series',
      description: '',
      csvText: '',
      is_published: true,
      metrics: [emptyChartMetric()],
      overview_html: '',
      relevance_html: '',
      equity_html: '',
      methodology_html: '',
      overview_title: '',
      relevance_title: '',
      equity_title: '',
      methodology_title: ''
    });
    setChartError(null);
    setChartModalOpen(true);
  };

  const openEditChart = (dataset: CustomDataset) => {
    setEditingDataset(dataset);
    const csvLines = ['label,value,series_label'];
    (dataset.rows ?? []).forEach((r) => {
      csvLines.push([r.label, String(r.value), r.series_label ?? ''].join(','));
    });
    const metrics = (dataset.metrics ?? []).slice(0, 4).map((m) => ({ label: m.label, value: m.value, color_hex: m.color_hex ?? null }));
    setChartForm({
      page_id: dataset.page_id,
      title: dataset.title,
      chart_type: dataset.chart_type,
      description: dataset.description || '',
      csvText: dataset.rows?.length ? csvLines.join('\n') : '',
      is_published: dataset.is_published,
      metrics: metrics.length ? metrics : [emptyChartMetric()],
      overview_html: dataset.overview_html || '',
      relevance_html: dataset.relevance_html || '',
      equity_html: dataset.equity_html || '',
      methodology_html: dataset.methodology_html || '',
      overview_title: dataset.overview_title || '',
      relevance_title: dataset.relevance_title || '',
      equity_title: dataset.equity_title || '',
      methodology_title: dataset.methodology_title || ''
    });
    setChartError(null);
    setChartModalOpen(true);
  };

  const updateChartMetric = (index: number, field: 'label' | 'value' | 'color_hex', value: string | null) => {
    setChartForm((prev) => {
      const next = [...prev.metrics];
      next[index] = { ...next[index], [field]: value ?? '' };
      return { ...prev, metrics: next };
    });
  };

  const addChartMetric = () => {
    setChartForm((prev) => ({
      ...prev,
      metrics: prev.metrics.length < 4 ? [...prev.metrics, emptyChartMetric()] : prev.metrics
    }));
  };

  const removeChartMetric = (index: number) => {
    setChartForm((prev) => ({
      ...prev,
      metrics: prev.metrics.filter((_, i) => i !== index).length ? prev.metrics.filter((_, i) => i !== index) : [emptyChartMetric()]
    }));
  };

  const closeChartModal = () => {
    setChartModalOpen(false);
    setEditingDataset(null);
  };

  const handleSaveChart = async () => {
    setChartError(null);
    if (!chartForm.title.trim()) {
      setChartError('Title is required.');
      return;
    }
    const parsed = parseCsvToRows(chartForm.csvText);
    if (!parsed || parsed.rows.length === 0) {
      setChartError('Add at least one data row. Use CSV with header: label column (e.g. year, category), value column (e.g. value, number_of_chargers), optional series (e.g. series_label, type).');
      return;
    }
    const rows = parsed.rows.map((r) => ({
      label: r.label,
      value: r.value,
      series_label: r.series_label ?? null
    }));
    const metricsFiltered = chartForm.metrics
      .filter((m) => m.label.trim() || m.value.trim())
      .slice(0, 4)
      .map((m, i) => ({ label: m.label.trim(), value: m.value.trim(), display_order: i, color_hex: m.color_hex || null }));
    setChartSaving(true);
    try {
      if (editingDataset) {
        const updated = await updateCustomDataset(
          editingDataset.id,
          {
            page_id: chartForm.page_id,
            title: chartForm.title.trim(),
            chart_type: chartForm.chart_type,
            description: chartForm.description.trim() || null,
            is_published: chartForm.is_published,
            overview_html: chartForm.overview_html.trim() || null,
            relevance_html: chartForm.relevance_html.trim() || null,
            equity_html: chartForm.equity_html.trim() || null,
            methodology_html: chartForm.methodology_html.trim() || null,
            overview_title: chartForm.overview_title.trim() || null,
            relevance_title: chartForm.relevance_title.trim() || null,
            equity_title: chartForm.equity_title.trim() || null,
            methodology_title: chartForm.methodology_title.trim() || null,
            x_axis_label: parsed.x_axis_label || null,
            y_axis_label: parsed.y_axis_label || null
          },
          rows,
          metricsFiltered,
          userId
        );
        if (updated) {
          await loadDatasets();
          closeChartModal();
        } else {
          setChartError('Failed to update chart.');
        }
      } else {
        const created = await createCustomDataset(
          {
            location_name: locationName,
            page_id: chartForm.page_id,
            title: chartForm.title.trim(),
            chart_type: chartForm.chart_type,
            description: chartForm.description.trim() || null,
            display_order: datasets.filter((d) => d.page_id === chartForm.page_id).length,
            is_published: chartForm.is_published,
            created_by: userId,
            overview_html: chartForm.overview_html.trim() || null,
            relevance_html: chartForm.relevance_html.trim() || null,
            equity_html: chartForm.equity_html.trim() || null,
            methodology_html: chartForm.methodology_html.trim() || null,
            overview_title: chartForm.overview_title.trim() || null,
            relevance_title: chartForm.relevance_title.trim() || null,
            equity_title: chartForm.equity_title.trim() || null,
            methodology_title: chartForm.methodology_title.trim() || null,
            x_axis_label: parsed.x_axis_label || null,
            y_axis_label: parsed.y_axis_label || null
          },
          rows,
          metricsFiltered,
          userId
        );
        if (created) {
          await loadDatasets();
          closeChartModal();
        } else {
          setChartError('Failed to create chart.');
        }
      }
    } finally {
      setChartSaving(false);
    }
  };

  const handleDeleteChart = async (dataset: CustomDataset) => {
    if (!window.confirm(`Delete chart "${dataset.title}"?`)) return;
    const ok = await deleteCustomDataset(dataset.id);
    if (ok) await loadDatasets();
  };

  const pageIds = getAllowedPageIds();
  const datasetPageIds = getAllowedDatasetPageIds();
  const chartTypes = getChartTypes();
  const sectionsByPage = pageIds.reduce<Record<CustomSectionPageId, CustomSection[]>>(
    (acc, pageId) => {
      acc[pageId] = sections.filter((s) => s.page_id === pageId);
      return acc;
    },
    {} as Record<CustomSectionPageId, CustomSection[]>
  );
  const datasetsByPage = datasetPageIds.reduce<Record<CustomDatasetPageId, CustomDataset[]>>(
    (acc, pageId) => {
      acc[pageId] = datasets.filter((d) => d.page_id === pageId);
      return acc;
    },
    {} as Record<CustomDatasetPageId, CustomDataset[]>
  );

  return (
    <div className="admin-custom-content">
      <div className="admin-settings-page-block">
        <div className="admin-custom-content-header">
          <h2>Custom content</h2>
          <p className="admin-custom-content-description">
            Add custom sections (e.g. local programs, key metrics) to Transportation, Energy &amp; Buildings, or People for this {scopeLabel || 'location'}. They appear at the bottom of each page and in PDF/Word reports.
          </p>
          <button type="button" className="admin-custom-content-add" onClick={() => openAdd()}>
            + Add custom section
          </button>
        </div>

        {loading ? (
          <div className="admin-settings-loading">Loading custom sections...</div>
        ) : (
          pageIds.map((pageId) => (
            <div key={pageId} className="admin-custom-content-page">
              <h3>{pageIdToLabel(pageId)}</h3>
              {sectionsByPage[pageId]?.length ? (
                <ul className="admin-custom-content-list">
                  {sectionsByPage[pageId].map((section) => (
                    <li key={section.id} className="admin-custom-content-item">
                      <span className="admin-custom-content-item-title">{section.section_title}</span>
                      <span className="admin-custom-content-item-slug">{section.section_slug}</span>
                      <div className="admin-custom-content-item-actions">
                        <button type="button" onClick={() => openEdit(section)}>
                          Edit
                        </button>
                        <button type="button" onClick={() => handleDelete(section)} className="admin-custom-content-delete">
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="admin-custom-content-empty">No custom sections. Add one above or per page.</p>
              )}
              <button type="button" className="admin-custom-content-add-inline" onClick={() => openAdd(pageId)}>
                + Add for {pageIdToLabel(pageId)}
              </button>
            </div>
          ))
        )}
      </div>

      <div className="admin-settings-page-block">
        <div className="admin-custom-content-header">
          <h2>Custom charts</h2>
          <p className="admin-custom-content-description">
            Upload CSV data to show as a time series, bar chart, or table on Transportation, Energy &amp; Buildings, or People. Use columns: label, value (optional: series_label for multiple series).
          </p>
          <button type="button" className="admin-custom-content-add" onClick={() => openAddChart()}>
            + Add custom chart
          </button>
        </div>

        {datasetsLoading ? (
          <div className="admin-settings-loading">Loading custom charts...</div>
        ) : (
          datasetPageIds.map((pageId) => (
            <div key={pageId} className="admin-custom-content-page">
              <h3>{pageIdToLabel(pageId)}</h3>
              {datasetsByPage[pageId]?.length ? (
                <ul className="admin-custom-content-list">
                  {datasetsByPage[pageId].map((dataset) => (
                    <li key={dataset.id} className="admin-custom-content-item">
                      <span className="admin-custom-content-item-title">{dataset.title}</span>
                      <span className="admin-custom-content-item-slug">{chartTypeToLabel(dataset.chart_type)}</span>
                      <div className="admin-custom-content-item-actions">
                        <button type="button" onClick={() => openEditChart(dataset)}>
                          Edit
                        </button>
                        <button type="button" onClick={() => handleDeleteChart(dataset)} className="admin-custom-content-delete">
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="admin-custom-content-empty">No custom charts. Add one above or per page.</p>
              )}
              <button type="button" className="admin-custom-content-add-inline" onClick={() => openAddChart(pageId)}>
                + Add for {pageIdToLabel(pageId)}
              </button>
            </div>
          ))
        )}
      </div>

      {modalOpen && (
        <div className="admin-custom-content-modal-overlay" onClick={closeModal}>
          <div className="admin-custom-content-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingSection ? 'Edit custom section' : 'Add custom section'}</h3>
            {error && <div className="admin-custom-content-error">{error}</div>}
            <div className="admin-custom-content-form">
              <label>
                Page
                <select
                  value={form.page_id}
                  onChange={(e) => updateForm({ page_id: e.target.value as CustomSectionPageId })}
                  disabled={!!editingSection}
                >
                  {pageIds.map((id) => (
                    <option key={id} value={id}>
                      {pageIdToLabel(id)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Title *
                <input
                  type="text"
                  value={form.section_title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g. Local EV Programs"
                />
              </label>
              <label>
                Slug (URL-friendly id)
                <input
                  type="text"
                  value={form.section_slug}
                  onChange={(e) => updateForm({ section_slug: e.target.value })}
                  placeholder="e.g. local-ev-programs"
                />
              </label>
              <label>
                Content (HTML or plain text)
                <textarea
                  value={form.content_html}
                  onChange={(e) => updateForm({ content_html: e.target.value })}
                  rows={6}
                  placeholder="<p>Your content here...</p>"
                />
              </label>
              <div className="admin-custom-content-narrative-tabs">
                <h4>Narrative tabs (optional)</h4>
                <p className="admin-custom-content-description" style={{ marginTop: -4, marginBottom: 12 }}>
                  Overview, Relevance, Equity, and Methodology. Tab titles are editable; leave blank to use defaults.
                </p>
                <label>
                  Overview — Tab title
                  <input
                    type="text"
                    value={form.overview_title}
                    onChange={(e) => updateForm({ overview_title: e.target.value })}
                    placeholder="Overview"
                  />
                </label>
                <label>
                  Overview — Content
                  <textarea
                    value={form.overview_html}
                    onChange={(e) => updateForm({ overview_html: e.target.value })}
                    rows={3}
                    placeholder="Overview text or HTML"
                  />
                </label>
                <label>
                  Relevance — Tab title
                  <input
                    type="text"
                    value={form.relevance_title}
                    onChange={(e) => updateForm({ relevance_title: e.target.value })}
                    placeholder="Relevance"
                  />
                </label>
                <label>
                  Relevance — Content
                  <textarea
                    value={form.relevance_html}
                    onChange={(e) => updateForm({ relevance_html: e.target.value })}
                    rows={3}
                    placeholder="Relevance text or HTML"
                  />
                </label>
                <label>
                  Equity — Tab title
                  <input
                    type="text"
                    value={form.equity_title}
                    onChange={(e) => updateForm({ equity_title: e.target.value })}
                    placeholder="Equity"
                  />
                </label>
                <label>
                  Equity — Content
                  <textarea
                    value={form.equity_html}
                    onChange={(e) => updateForm({ equity_html: e.target.value })}
                    rows={3}
                    placeholder="Equity text or HTML"
                  />
                </label>
                <label>
                  Methodology — Tab title
                  <input
                    type="text"
                    value={form.methodology_title}
                    onChange={(e) => updateForm({ methodology_title: e.target.value })}
                    placeholder="Methodology"
                  />
                </label>
                <label>
                  Methodology — Content
                  <textarea
                    value={form.methodology_html}
                    onChange={(e) => updateForm({ methodology_html: e.target.value })}
                    rows={3}
                    placeholder="Methodology text or HTML"
                  />
                </label>
              </div>
              <label>
                Image URL
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => updateForm({ image_url: e.target.value })}
                  placeholder="https://..."
                />
              </label>
              <label>
                Link URL
                <input
                  type="url"
                  value={form.link_url}
                  onChange={(e) => updateForm({ link_url: e.target.value })}
                  placeholder="https://..."
                />
              </label>
              <label>
                Link label
                <input
                  type="text"
                  value={form.link_label}
                  onChange={(e) => updateForm({ link_label: e.target.value })}
                  placeholder="e.g. Learn more"
                />
              </label>
              <label className="admin-custom-content-checkbox">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => updateForm({ is_published: e.target.checked })}
                />
                Published (visible on page)
              </label>

              <div className="admin-custom-content-metrics">
                <h4>Key metrics (optional)</h4>
                {form.metrics.map((m, i) => (
                  <div key={i} className="admin-custom-content-metric-row">
                    <input
                      type="text"
                      value={m.label}
                      onChange={(e) => updateMetric(i, 'label', e.target.value)}
                      placeholder="Label"
                    />
                    <input
                      type="text"
                      value={m.value}
                      onChange={(e) => updateMetric(i, 'value', e.target.value)}
                      placeholder="Value"
                    />
                    <input
                      type="text"
                      value={m.color_hex || ''}
                      onChange={(e) => updateMetric(i, 'color_hex', e.target.value)}
                      placeholder="#hex (optional)"
                    />
                    <button type="button" onClick={() => removeMetric(i)} disabled={form.metrics.length <= 1}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addMetric}>+ Add metric</button>
              </div>
            </div>
            <div className="admin-custom-content-modal-actions">
              <button type="button" onClick={closeModal}>
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {chartModalOpen && (
        <div className="admin-custom-content-modal-overlay" onClick={closeChartModal}>
          <div className="admin-custom-content-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingDataset ? 'Edit custom chart' : 'Add custom chart'}</h3>
            {chartError && <div className="admin-custom-content-error">{chartError}</div>}
            <div className="admin-custom-content-form">
              <label>
                Page
                <select
                  value={chartForm.page_id}
                  onChange={(e) => setChartForm((f) => ({ ...f, page_id: e.target.value as CustomDatasetPageId }))}
                  disabled={!!editingDataset}
                >
                  {datasetPageIds.map((id) => (
                    <option key={id} value={id}>
                      {pageIdToLabel(id)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Title *
                <input
                  type="text"
                  value={chartForm.title}
                  onChange={(e) => setChartForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Local program participation"
                />
              </label>
              <label>
                Chart type
                <select
                  value={chartForm.chart_type}
                  onChange={(e) => setChartForm((f) => ({ ...f, chart_type: e.target.value as CustomDatasetChartType }))}
                >
                  {chartTypes.map((t) => (
                    <option key={t} value={t}>
                      {chartTypeToLabel(t)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Description (optional)
                <input
                  type="text"
                  value={chartForm.description}
                  onChange={(e) => setChartForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Short description"
                />
              </label>
              <label>
                Data (CSV with header row; flexible column names supported)
                <textarea
                  value={chartForm.csvText}
                  onChange={(e) => setChartForm((f) => ({ ...f, csvText: e.target.value }))}
                  rows={8}
                  placeholder={'year,number_of_chargers,type\n2020,22,Level 2\n2020,2,Fast\n2021,23,Level 2'}
                />
              </label>
              <p className="admin-custom-content-description" style={{ marginTop: -8, marginBottom: 12 }}>
                First row = header. Label column: year, label, category, etc. Value column: value, number_of_chargers, count, etc. Optional series: type, series_label, series.
              </p>
              <label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setChartForm((f) => ({ ...f, csvText: String(reader.result ?? '') }));
                    reader.readAsText(file);
                    e.target.value = '';
                  }}
                />
                Upload CSV file
              </label>

              <div className="admin-custom-content-metrics">
                <h4>Key metrics (optional, 1–4)</h4>
                {chartForm.metrics.map((m, i) => (
                  <div key={i} className="admin-custom-content-metric-row">
                    <input
                      type="text"
                      value={m.label}
                      onChange={(e) => updateChartMetric(i, 'label', e.target.value)}
                      placeholder="Label"
                    />
                    <input
                      type="text"
                      value={m.value}
                      onChange={(e) => updateChartMetric(i, 'value', e.target.value)}
                      placeholder="Value"
                    />
                    <input
                      type="text"
                      value={m.color_hex || ''}
                      onChange={(e) => updateChartMetric(i, 'color_hex', e.target.value || null)}
                      placeholder="#hex (optional)"
                    />
                    <button type="button" onClick={() => removeChartMetric(i)} disabled={chartForm.metrics.length <= 1}>
                      Remove
                    </button>
                  </div>
                ))}
                {chartForm.metrics.length < 4 && (
                  <button type="button" onClick={addChartMetric}>+ Add metric</button>
                )}
              </div>

              <div className="admin-custom-content-narrative-tabs">
                <h4>Narrative tabs (optional)</h4>
                <p className="admin-custom-content-description" style={{ marginTop: -4, marginBottom: 12 }}>
                  Tab titles are editable; leave blank to use defaults (Overview, Relevance, Equity, Methodology).
                </p>
                <label>
                  Overview — Tab title
                  <input
                    type="text"
                    value={chartForm.overview_title}
                    onChange={(e) => setChartForm((f) => ({ ...f, overview_title: e.target.value }))}
                    placeholder="Overview"
                  />
                </label>
                <label>
                  Overview — Content
                  <textarea
                    value={chartForm.overview_html}
                    onChange={(e) => setChartForm((f) => ({ ...f, overview_html: e.target.value }))}
                    rows={3}
                    placeholder="Overview text or HTML"
                  />
                </label>
                <label>
                  Relevance — Tab title
                  <input
                    type="text"
                    value={chartForm.relevance_title}
                    onChange={(e) => setChartForm((f) => ({ ...f, relevance_title: e.target.value }))}
                    placeholder="Relevance"
                  />
                </label>
                <label>
                  Relevance — Content
                  <textarea
                    value={chartForm.relevance_html}
                    onChange={(e) => setChartForm((f) => ({ ...f, relevance_html: e.target.value }))}
                    rows={3}
                    placeholder="Relevance text or HTML"
                  />
                </label>
                <label>
                  Equity — Tab title
                  <input
                    type="text"
                    value={chartForm.equity_title}
                    onChange={(e) => setChartForm((f) => ({ ...f, equity_title: e.target.value }))}
                    placeholder="Equity"
                  />
                </label>
                <label>
                  Equity — Content
                  <textarea
                    value={chartForm.equity_html}
                    onChange={(e) => setChartForm((f) => ({ ...f, equity_html: e.target.value }))}
                    rows={3}
                    placeholder="Equity text or HTML"
                  />
                </label>
                <label>
                  Methodology — Tab title
                  <input
                    type="text"
                    value={chartForm.methodology_title}
                    onChange={(e) => setChartForm((f) => ({ ...f, methodology_title: e.target.value }))}
                    placeholder="Methodology"
                  />
                </label>
                <label>
                  Methodology — Content
                  <textarea
                    value={chartForm.methodology_html}
                    onChange={(e) => setChartForm((f) => ({ ...f, methodology_html: e.target.value }))}
                    rows={3}
                    placeholder="Methodology text or HTML"
                  />
                </label>
              </div>

              <label className="admin-custom-content-checkbox">
                <input
                  type="checkbox"
                  checked={chartForm.is_published}
                  onChange={(e) => setChartForm((f) => ({ ...f, is_published: e.target.checked }))}
                />
                Published (visible on page)
              </label>
            </div>
            <div className="admin-custom-content-modal-actions">
              <button type="button" onClick={closeChartModal}>
                Cancel
              </button>
              <button type="button" onClick={handleSaveChart} disabled={chartSaving}>
                {chartSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomContent;
